import React from 'react'

import { Validation, ValidationTypeInfo, commonValidationTypes, validationTypes } from 'components/dashboard/product-template-table/utils/interfaces'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown'
import { Fieldset } from 'primereact/fieldset'
import { InputText } from 'primereact/inputtext'
import { Tooltip } from 'react-tooltip'
import { CellType } from 'utils/enums'
import TimeSpanInputs from './TimeSpanInputs'

const secondsOfDayTime = (date: Date, excludeSeconds: boolean = false) => {
  if (!(date instanceof Date)) return 0;
  const seconds = excludeSeconds ? 0 : date.getSeconds();
  return (date.getHours() * 60 + date.getMinutes()) * 60 + seconds;
}

const toDateTime = (secs: number | undefined) => {
  if (secs === undefined) return null;
  const d = new Date(1970, 0, 1);
  d.setSeconds(secs);
  return d;
}

type ValidationOptionsProps = {
  validators: Validation[],
  cellType: CellType,
  onValidationChange: (validations: Validation[]) => void,
}

const ValidationOptions: React.FC<ValidationOptionsProps> = (props) => {
  const { cellType, validators, onValidationChange } = props;

  const addValidationOption = () => {
    // @ts-expect-error
    onValidationChange([...validators, { message: "", type: "", value: "" }]);
  }

  const handleValidationChange = (
    e: React.ChangeEvent<HTMLInputElement> | DropdownChangeParams,
    index: number
  ) => {
    let { value } = e.target;
    const { name } = e.target;
    
    if (isTimeType && name === "value") {
      value = secondsOfDayTime(value, true).toString();
    }

    onValidationChange(validators.map((v, i) => (i === index ? { ...v, [name]: value } : v)));
  };

  const isTimeSpanType = cellType === CellType.TIMESPAN;
  const isTimeType = cellType === CellType.TIME;

  let validationOptions: Readonly<ValidationTypeInfo[]> = [];
  switch (cellType) {
    case CellType.LONG:
    case CellType.DOUBLE:
    case CellType.TIME:
    case CellType.TIMESPAN:
    case CellType.DATETIME:
    case CellType.INSTANT:
      validationOptions = validationTypes;
      break;
    default:
      validationOptions = commonValidationTypes;
      break;
  }

  const mappedValidationOptions = validators.map((validator, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <div className="flex flex-nowrap justify-content-between gap-4 mt-1" key={i}>
      <div className="p-field flex-1">
        {i === 0 && <label htmlFor="message">Message</label>}
        <InputText
          id="message"
          value={validator.message ?? ""}
          name="message"
          onChange={e => handleValidationChange(e, i)}
          placeholder="e.g. Item quantity must be greater than 0"
          className="p-inputtext-sm"
          style={{ blockSize: "2.8rem" }}
        />
      </div>

      <div className="p-field">
        {i === 0 && <label htmlFor="type">Validation</label>}
        <Dropdown
          id="type"
          value={validator.type}
          placeholder="Select validation"
          name="type"
          onChange={e => handleValidationChange(e, i)}
          options={validationOptions as ValidationTypeInfo[]}
          optionValue="short"
          optionLabel="long"
          className="p-inputtext-sm w-13rem"
          style={{ blockSize: "2.8rem" }}
        />
      </div>

      <div className="p-field flex-1">
        {i === 0 && <label htmlFor="value">Value</label>}
        {!isTimeType && !isTimeSpanType && (
          <InputText
            id="value"
            value={validator.value}
            name="value"
            onChange={e => handleValidationChange(e, i)}
            className="p-inputtext-sm flex-1"
            style={{ blockSize: "2.8rem" }}
          />
        )}
        {isTimeSpanType && (
          <div className="p-inputgroup">
            <TimeSpanInputs
              value={parseFloat(validator.value!)}
              onChange={value =>
                onValidationChange(
                  validators.map((v, ind) => (i === ind ? { ...v, value: value.toString() } : v))
                )
              }
            />
          </div>
        )}
        {isTimeType && (
          <Calendar
            id="value"
            value={toDateTime(Number(validator.value))}
            onChange={e => handleValidationChange(e, i)}
            timeOnly
            name="value"
            className="p-inputtext-sm w-13rem"
            style={{ blockSize: "2.8rem" }}
          />
        )}
      </div>
      <div className="p-field align-self-end">
        <Button
          icon="pi pi-trash"
          onClick={() => onValidationChange(validators.toSpliced(i, 1))}
          className="p-button-outlined p-button-danger"
        />
      </div>
    </div>
  ));

  return (
    <Fieldset legend="Validation Options" className="p-col-12 mt-3" toggleable>
    {mappedValidationOptions}

    <div className="p-field flex-1">
      <Tooltip id="add-validation" />
      <Button
        data-tooltip-id="add-validation"
        data-tooltip-content="Add new validation row"
        data-tooltip-place="right"
        icon="pi pi-plus"
        onClick={addValidationOption}
        className="p-button-outlined p-button-success p-button-rounded"
      />
    </div>
  </Fieldset>
  )
}

export default ValidationOptions